// Two access tiers gated by hardcoded UUIDs.
//
//   isOwner(user)         — the single owner. Reserved for surfaces
//                           that are truly private to the project
//                           creator: the Journey recovery log, the
//                           reports' journey block, the Settings →
//                           Developer section, the AI-coach debug
//                           panel. Knowing the UUID is not a secret —
//                           RLS already locks the data — but the
//                           client gate keeps those UI affordances
//                           from ever appearing in other accounts.
//
//   hasOwnerAccess(user)  — owner OR any UUID in the trusted-guest
//                           list. Used for owner-tier UI that's been
//                           explicitly shared: the English module,
//                           Learning shelf, Immigration tracker, the
//                           news-source admin button + page, the
//                           per-article English drill, the global
//                           selection pill.
//
// Override via env at build time:
//   REACT_APP_OWNER_USER_ID         — replaces the owner UUID
//   REACT_APP_OWNER_GUEST_IDS       — comma-separated list of guest UUIDs
const FALLBACK_OWNER_ID = 'cb846c7b-e9f3-4d5c-8390-9598c4ae4ece'
// No trusted guests. The pair/couples surfaces resolve to solo (no
// partner) until a guest UUID is added back here or via env.
const FALLBACK_GUEST_IDS = []

const OWNER_ID =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_OWNER_USER_ID)
  || FALLBACK_OWNER_ID

const GUEST_IDS = (() => {
  const raw = typeof process !== 'undefined' && process.env && process.env.REACT_APP_OWNER_GUEST_IDS
  if (!raw) return FALLBACK_GUEST_IDS
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
})()

export function isOwner(user) {
  if (!OWNER_ID || !user?.id) return false
  return user.id === OWNER_ID
}

export function hasOwnerAccess(user) {
  if (!user?.id) return false
  if (user.id === OWNER_ID) return true
  return GUEST_IDS.includes(user.id)
}

// Symmetric trusted-pair lookup: returns the other user in the pair,
// or null if the caller isn't part of it. Currently the pair is
// owner ↔ first guest; if the guest list grows, this picks the first
// guest as the canonical partner (good enough until/unless we
// introduce a real "select your partner" UI).
//
// Used by the Wordle partner panel + (future) shared dashboard to
// know whose row to subscribe to / read against.
export function getPartnerId(user) {
  if (!user?.id) return null
  if (user.id === OWNER_ID) return GUEST_IDS[0] || null
  if (GUEST_IDS.includes(user.id)) return OWNER_ID
  return null
}

// First-name map for the trusted pair, used wherever the UI wants
// to address the OTHER person by name ("the partner is playing now…"). Two
// entries today, each bilingual so the partner row reads in whichever
// locale the viewer is using — both partners are Arabic-primary so
// the AR rendering is the everyday case, the EN form is what shows
// up if they ever switch the UI locale.
//
// If the trusted list ever grows beyond the owner ↔ guest pair, this
// belongs in a real `profiles.display_name` column with per-locale
// columns.
const KNOWN_NAMES = {
  [OWNER_ID]: { en: 'Omar', ar: 'عمر' },
}

export function getDisplayName(userId, locale = 'en') {
  if (!userId) return null
  const entry = KNOWN_NAMES[userId]
  if (!entry) return null
  return entry[locale] || entry.en
}
